import {computed, ComputedRef, onMounted, onUnmounted, reactive, ref} from 'vue';
import type {EntityType} from '@/types/UniversalSearch';
import type {TabConfig} from '@/types/EntityTabs';
import {
  LayoutDashboard,
  FileText,
  Building2,
  Folder,
  ChartLine,
  Database,
  AlertCircle,
  Calculator,
  RefreshCw,
  Files,
  BarChart3,
  CheckCircle,
  Star,
  ChartColumnStacked,
} from 'lucide-vue-next';
import {UserPermission} from '@/types/UserPermissions';
import {towerService} from '@/services/v2/TowerService';
import {documentService} from '@/services/v2/DocumentService';
import {useUser} from './useUser';
import {UserRole} from '@/types/UserRole';
import {MutationPayload, useStore} from 'vuex';
import {StoreMutationType} from '@/types/StoreMutationType';

export const useEntityTabs = () => {
  const store = useStore();
  const entityData = reactive<{type?: EntityType; id?: string;}>({});
  const loading = ref(false);
  const error = ref<string | undefined>();
  const user = useUser();
  const hasPublishedTowers = ref<boolean>(false);
  const hasClientVisibleDocuments = ref<boolean>(false);

  let unsubscribeToStore: (() => void) | undefined;

  // ****************
  // lifecycle hooks
  // ****************
  onMounted(() => {
    unsubscribeToStore = store.subscribe(handleClientViewChange)
  });

  onUnmounted(() => {
    unsubscribeToStore?.();
  });

  async function handleClientViewChange(mutation: MutationPayload) {
    if (mutation.type === StoreMutationType.ENABLE_CLIENT_VIEW_MODE) {
      if (entityData.type && entityData.id) {
        if (entityData.type === 'account') {
          hasPublishedTowers.value = (await towerService.getPublishedTowerCountByAccount(parseInt(entityData.id))) > 0;
          hasClientVisibleDocuments.value = (await documentService.getClientVisibleDocumentCountByAccount(parseInt(entityData.id))) > 0;
        } else if (entityData.type === 'parent-account') {
          hasClientVisibleDocuments.value = (await documentService.getClientVisibleDocumentCountByAccount(parseInt(entityData.id), true)) > 0;
        }
      }
    }
  }

  // Create reactive permission context based on user role
  const isAClientAccount = computed(() => {
    return user.role.value === UserRole.Client;
  });

  const canViewClaims = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.ClaimsRead);
    }
    return true;
  });

  const canViewQuotes = computed(() => {
    return user.hasPermissions(UserPermission.QuotesWrite);
  });

  const canViewAnalytics = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.AnalyticsRead);
    }
    return true;
  });

  const canViewDocuments = computed(() => {
    // for clients only show if client-viewable documents exist OR if user has write permission
    if (isAClientAccount.value) {
      return (
        (user.hasPermissions(UserPermission.DocumentsRead) && hasClientVisibleDocuments.value) ||
        user.hasPermissions(UserPermission.DocumentsWrite)
      );
    }
    return true;
  });

  const canViewPolicies = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.PoliciesRead);
    }
    return true;
  });

  const canViewPrograms = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.ProgramsRead);
    }
    return true;
  });

  const canViewOverview = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.OverviewRead);
    }
    return true;
  });

  const canViewCardView = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.CardviewRead);
    }
    return true;
  })

  const canViewTowers = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.TowersRead) && hasPublishedTowers.value;
    }
    return true;
  });

  const canViewRenewal = computed(() => {
    if (isAClientAccount.value) {
      return user.hasPermissions(UserPermission.RenewalRead);
    }
    return true;
  });

  // Fetch async data for account tabs
  const fetchAccountTabData = async (accountId: string): Promise<void> => {
    try {
      if (user.hasPermissions(UserPermission.TowersRead)) {
        hasPublishedTowers.value = (await towerService.getPublishedTowerCountByAccount(parseInt(accountId))) > 0;
      }
      if (user.hasPermissions(UserPermission.DocumentsRead)) {
        hasClientVisibleDocuments.value =
          (await documentService.getClientVisibleDocumentCountByAccount(parseInt(accountId))) > 0;
      }
    } catch (err) {
      console.warn('Error fetching async tab data:', err);
      // Continue with defaults
    }
  };

  // Fetch async data for parent account tabs
  const fetchParentAccountTabData = async (accountId: string): Promise<void> => {
    try {
      if (user.hasPermissions(UserPermission.DocumentsRead)) {
        hasClientVisibleDocuments.value =
          (await documentService.getClientVisibleDocumentCountByAccount(parseInt(accountId), true)) > 0;
      }
    } catch (err) {
      console.warn('Error fetching async tab data:', err);
      // Continue with defaults
    }
  };

  // Create reactive account tabs
  const createAccountTabs = (accountId: string): ComputedRef<TabConfig[]> => {
    return computed(() =>
      [
        {
          key: 'overview',
          label: 'Overview',
          route: `/account/${accountId}/overview`,
          visible: canViewOverview.value,
          icon: LayoutDashboard,
        },
        {
          key: 'policy-detail',
          label: 'Policies',
          route: `/account/${accountId}/policy-detail`,
          visible: canViewPolicies.value,
          icon: FileText,
        },
        {
          key: 'towers',
          label: 'Towers',
          route: `/account/${accountId}/towers`,
          visible: canViewTowers.value,
          icon: ChartColumnStacked,
        },
        {
          key: 'programs',
          label: 'Programs',
          route: `/account/${accountId}/programs`,
          visible: canViewPrograms.value,
          icon: Folder,
        },
        {
          key: 'benchmarking',
          label: 'Benchmarking',
          route: `/account/${accountId}/benchmarking`,
          visible: !isAClientAccount.value,
          icon: ChartLine,
        },
        {
          key: 'exposure-data',
          label: 'Exposure',
          route: `/account/${accountId}/exposure-data`,
          visible: !isAClientAccount.value,
          icon: Database,
        },
        {
          key: 'claim-detail',
          label: 'Claims',
          route: `/account/${accountId}/claim-detail`,
          visible: canViewClaims.value,
          icon: AlertCircle,
        },
        {
          key: 'quotes',
          label: 'Quotes',
          route: `/account/${accountId}/quotes`,
          visible: canViewQuotes.value,
          icon: Calculator,
        },
        {
          key: 'renewal',
          label: 'Renewal',
          route: `/account/${accountId}/renewal`,
          visible: canViewRenewal.value,
          icon: RefreshCw,
        },
        {
          key: 'documents',
          label: 'Documents',
          route: `/account/${accountId}/documents`,
          visible: canViewDocuments.value,
          icon: Files,
        },
        {
          key: 'analytics',
          label: 'Analytics',
          route: `/account/${accountId}/analytics`,
          visible: canViewAnalytics.value,
          icon: BarChart3,
        },
        {
          key: 'client-readiness',
          label: 'Client Readiness',
          route: `/account/${accountId}/client-readiness`,
          visible: !isAClientAccount.value,
          icon: CheckCircle,
        },
      ].filter((tab) => tab.visible),
    );
  };

  // Create reactive parent account tabs
  const createParentAccountTabs = (accountId: string): ComputedRef<TabConfig[]> => {
    return computed(() =>
      [
        {
          key: 'overview',
          label: 'Overview',
          route: `/parent/account/${accountId}/overview`,
          visible: canViewOverview.value,
          icon: LayoutDashboard,
        },
        {
          key: 'card-view',
          label: 'Card View',
          route: `/parent/account/${accountId}/card-view`,
          visible: canViewCardView.value,
          icon: Building2,
        },
        {
          key: 'policy-detail',
          label: 'Policies',
          route: `/parent/account/${accountId}/policy-detail`,
          visible: canViewPolicies.value,
          icon: FileText,
        },
        {
          key: 'claim-detail',
          label: 'Claims',
          route: `/parent/account/${accountId}/claim-detail`,
          visible: canViewClaims.value,
          icon: AlertCircle,
        },
        {
          key: 'documents',
          label: 'Documents',
          route: `/parent/account/${accountId}/documents`,
          visible: canViewDocuments.value,
          icon: Files,
        },
      ].filter((tab) => tab.visible),
    );
  };

  const getCarrierTabs = (carrierId: string): ComputedRef<TabConfig[]> => {
    return computed(() =>
      [
        {
          key: 'overview',
          label: 'Overview',
          route: `/carrier/${carrierId}/overview`,
          visible: true,
          icon: LayoutDashboard,
        },
        {
          key: 'policy-detail',
          label: 'Policies',
          route: `/carrier/${carrierId}/policy-detail`,
          visible: true,
          icon: FileText,
        },
        {
          key: 'insurer-ratings',
          label: 'Insurer Ratings',
          route: `/carrier/${carrierId}/insurer-ratings`,
          visible: true,
          icon: Star,
        },
      ].filter((tab) => tab.visible),
    );
  };

  const getWholesalerTabs = (wholesalerId: string): ComputedRef<TabConfig[]> => {
    return computed(() =>
      [
        {
          key: 'overview',
          label: 'Overview',
          route: `/wholesaler-group/${wholesalerId}/overview`,
          visible: true,
          icon: LayoutDashboard,
        },
        {
          key: 'policy-detail',
          label: 'Policies',
          route: `/wholesaler-group/${wholesalerId}/policy-detail`,
          visible: true,
          icon: FileText,
        },
      ].filter((tab) => tab.visible),
    );
  };

  const getCustomWholesalerTabs = (customWholesalerId: string): ComputedRef<TabConfig[]> => {
    return computed(() =>
      [
        {
          key: 'overview',
          label: 'Overview',
          route: `/wholesaler-group/custom/${customWholesalerId}/overview`,
          visible: true,
          icon: LayoutDashboard,
        },
        {
          key: 'policy-detail',
          label: 'Policies',
          route: `/wholesaler-group/custom/${customWholesalerId}/policy-detail`,
          visible: true,
          icon: FileText,
        },
      ].filter((tab) => tab.visible),
    );
  };

  const getTabs = async (entityType: EntityType, entityId: string): Promise<ComputedRef<TabConfig[]>> => {
    entityData.type = entityType;
    entityData.id = entityId;
    loading.value = true;
    error.value = undefined;

    try {
      switch (entityType) {
        case 'account':
          // Fetch async data first
          await fetchAccountTabData(entityId);
          // Return reactive computed tabs
          return createAccountTabs(entityId);
        case 'parent-account':
          await fetchParentAccountTabData(entityId);
          return createParentAccountTabs(entityId);
        case 'carrier':
          return getCarrierTabs(entityId);
        case 'wholesaler':
          return getWholesalerTabs(entityId);
        case 'customWholesaler':
          return getCustomWholesalerTabs(entityId);
        default:
          return computed(() => []);
      }
    } catch (err) {
      error.value = 'Failed to load navigation tabs';
      console.error('Error loading entity tabs:', err);
      return computed(() => []);
    } finally {
      loading.value = false;
    }
  };

  return {
    getTabs,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };
};
